export const USER_AUDIT_FIELDS = ['name', 'email', 'rol', 'status', 'defaultStore']

export const USER_AUDIT_FIELD_LABELS = {
  name: 'Nombre',
  email: 'Email',
  rol: 'Rol',
  status: 'Estado',
  defaultStore: 'Sucursal por defecto',
}

export const USER_AUDIT_ACTION_LABELS = {
  created: 'Creación',
  updated: 'Actualización',
  password_reset_sent: 'Reset de contraseña enviado',
  password_changed: 'Contraseña cambiada',
  deleted: 'Eliminación',
}

/**
 * @param {Object} actor - Usuario autenticado (Vuex currentUser)
 * @returns {{ uid: string, email: string, nombre: string }}
 */
export const buildUserActor = actor => ({
  uid: actor.uid,
  email: actor.email || '',
  nombre: actor.nombre || actor.name || actor.email || actor.uid,
})

/**
 * @param {Object|null|undefined} actor
 * @returns {{ valid: boolean, actor?: Object, error?: string }}
 */
export const validateUserActor = actor => {
  if (!actor || !actor.uid) {
    return {
      valid: false,
      error: 'No se pudo identificar al usuario. Vuelva a iniciar sesión.',
    }
  }

  return {
    valid: true,
    actor: buildUserActor(actor),
  }
}

/**
 * @param {Object} data
 * @returns {Object}
 */
export const normalizeUserForAudit = data => ({
  name: String(data.name ?? '').trim(),
  email: String(data.email ?? '').trim(),
  rol: String(data.rol ?? '').trim(),
  status: String(data.status ?? '').trim(),
  defaultStore: String(data.defaultStore ?? '').trim(),
})

/**
 * @param {Object} before
 * @param {Object} after
 * @returns {Object}
 */
export const computeUserChanges = (before, after) => {
  const changes = {}

  USER_AUDIT_FIELDS.forEach(field => {
    const from = before[field]
    const to = after[field]
    if (from !== to) {
      changes[field] = { from, to }
    }
  })

  return changes
}

/**
 * @param {'created'|'updated'|'password_reset_sent'|'password_changed'|'deleted'} action
 * @param {Object} actor
 * @param {Object|null} changes
 * @param {Object|null} snapshot
 * @param {number} [timestamp]
 * @param {Object|null} [meta]
 * @returns {Object}
 */
export const buildUserAuditEntry = (
  action,
  actor,
  changes = null,
  snapshot = null,
  timestamp = Date.now(),
  meta = null
) => {
  const entry = {
    action,
    at: timestamp,
    by: actor,
  }

  if (changes && Object.keys(changes).length > 0) {
    entry.changes = changes
  }

  if (snapshot) {
    entry.snapshot = snapshot
  }

  if (meta && Object.keys(meta).length > 0) {
    entry.meta = meta
  }

  return entry
}

/**
 * @param {Object|null|undefined} auditLog
 * @returns {Array<Object>}
 */
export const getSortedUserAuditLogEntries = auditLog => {
  if (!auditLog || typeof auditLog !== 'object') {
    return []
  }

  return Object.entries(auditLog)
    .map(([id, entry]) => ({ id, ...entry }))
    .sort((a, b) => (b.at || 0) - (a.at || 0))
}

/**
 * @param {Object|null|undefined} actor
 * @returns {string}
 */
export const formatUserActor = actor => {
  if (!actor) return 'N/A'
  return actor.nombre || actor.email || actor.uid || 'N/A'
}

/**
 * Etiqueta legible de defaultStore para auditoría y UI.
 * "0" = acceso a todas las sucursales; vacío/ausente = sin sucursal asignada.
 *
 * @param {*} value
 * @param {Array<{ division?: string|number, id?: string|number, sucursal?: string }>} [stores]
 * @returns {string}
 */
export const formatDefaultStoreForAudit = (value, stores = []) => {
  if (value === undefined || value === null || value === '') {
    return 'Sin sucursal'
  }

  if (value === '0' || value === 0) {
    return 'Todas las sucursales'
  }

  const store = stores.find(
    s => String(s.division ?? s.id ?? '') === String(value)
  )
  return store?.sucursal || `División ${value}`
}
